import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { AppEvents } from '@/db/events';
import { useActivityMonitor } from '../hooks/useActivityMonitor';
import { beginEncryptedAccess, endEncryptedAccess, ensureDatabaseOpen } from '@/db/db';
import {
  registerPasskey as registerPasskeyAuth,
  authenticatePasskey,
  isPasskeyRegistered,
  type PasskeyMethod,
  PasskeyError,
} from '../lib/webauthn';

import { maybeMigrateLegacyCredential } from '../lib/passkey-migration';
import { getCurrentUser, getCurrentPerson } from '@/db/services/user_service';

import { syncSentryUser } from '@/lib/sentry-user';
import { checkSessionValidity, softSignOut } from '../lib/auth';
import { createLogger, LogLevel } from '../lib/logger';
import { BIOMETRICS_DISABLED } from '../config/env';
import { trackStorageCheck } from '@/db/services/analytics_service';
import { getSessionTracker } from '../lib/session-tracker';

const DEFAULT_LOCK_TIMEOUT_MS = 10 * 60 * 1000; // Fallback when org has no timeout configured

const logger = createLogger('LockContext', LogLevel.DEBUG);

/**
 * Lock context value interface
 */
interface LockContextValue {
  /** Whether the screen is currently locked */
  isLocked: boolean;

  /** Whether passkey authentication is registered */
  isRegistered: boolean;

  /**
   * True from the moment unlockScreen is invoked until it resolves. Lets
   * external consumers (e.g. the SW update banner) avoid actions that would
   * disrupt an in-flight WebAuthn ceremony — a reload mid-unlock kills it.
   */
  unlockInProgress: boolean;

  /** Lock the screen and disable encryption */
  lockScreen: () => Promise<void>;

  /** Unlock the screen with passkey authentication */
  unlockScreen: () => Promise<boolean>;

  /** Register passkey authentication for the first time */
  registerPasskey: (method?: PasskeyMethod) => Promise<boolean>;

  /** Reset the activity timer (manual activity signal) */
  resetActivityTimer: () => void;

  /** Whether the context is still loading initial state */
  isLoading: boolean;
}

const LockContext = createContext<LockContextValue | null>(null);

/**
 * Lock provider component
 * Manages screen locking, authentication (passkey or TOTP), and encryption state
 */
export function LockProvider({ children }: { children: React.ReactNode }) {
  const [isLocked, setIsLocked] = useState(true); // Start locked until we verify registration
  const [isRegistered, setIsRegistered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [unlockInProgress, setUnlockInProgress] = useState(false);
  const [lockTimeoutMs, setLockTimeoutMs] = useState(DEFAULT_LOCK_TIMEOUT_MS);

  // Track if we've already initialized to prevent double execution in React Strict Mode
  const hasInitialized = useRef(false);

  // Check registration status on mount
  useEffect(() => {
    // Prevent double execution in React Strict Mode
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const checkRegistration = async () => {
      // If biometrics is disabled (e.g., for testing), skip all registration checks
      if (BIOMETRICS_DISABLED) {
        logger.warn('Biometrics disabled via VITE_DISABLE_BIOMETRICS - skipping registration');
        setIsRegistered(true); // Pretend we're registered so AuthWrapper doesn't show modal
        setIsLocked(false); // Never locked
        setIsLoading(false);
        return;
      }

      try {
        // Ensure database is open before checking registration
        await ensureDatabaseOpen();

        // Check passkey registration (WebAuthn with PIN/password fallback)
        const passkeyRegistered = await isPasskeyRegistered();

        setIsRegistered(passkeyRegistered);

        if (passkeyRegistered) {
          // Registered — locked until user unlocks via passkey
          setIsLocked(true);
        } else {
          // If not registered, we're not locked (will show registration modal)
          setIsLocked(false);
        }
      } catch (error) {
        logger.error('Failed to check registration status:', error);
        setIsRegistered(false);
        setIsLocked(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkRegistration();
  }, []);

  // Load the lock timeout from the selected person's organization
  useEffect(() => {
    const loadOrgTimeout = async () => {
      try {
        const person = await getCurrentPerson();
        const orgSeconds = person?.organization?.session_timeout_in_seconds ?? 0;
        if (orgSeconds > 0) {
          setLockTimeoutMs(orgSeconds * 1000);
        }
      } catch (error) {
        logger.error('Failed to load org session timeout:', error);
      }
    };

    loadOrgTimeout();
  }, []);

  // Lock screen handler for activity monitor
  const handleTimeout = useCallback(async () => {
    logger.log('Activity timeout triggered - locking screen');
    await lockScreen();
  }, []);

  // Activity monitor - only enabled when unlocked and registered
  // Disabled entirely when BIOMETRICS_DISABLED is true
  const { resetTimer } = useActivityMonitor({
    timeoutMs: lockTimeoutMs,
    onTimeout: handleTimeout,
    enabled: !BIOMETRICS_DISABLED && !isLocked && isRegistered && !isLoading,
  });

  /**
   * Lock the screen
   */
  const lockScreen = useCallback(async () => {
    try {
      logger.log('Locking screen...');

      // End the current session — each unlock starts a fresh one. Done before
      // endEncryptedAccess() so the DB is still open and trackEvent() can
      // persist session.end locally.
      await getSessionTracker().endSession();

      // End encrypted access (clears the key from memory, data remains encrypted on disk)
      await endEncryptedAccess();

      // Update lock state
      setIsLocked(true);

      // Broadcast lock event to other tabs
      try {
        const channel = new BroadcastChannel('monami_lock_sync');
        try {
          channel.postMessage({ type: 'lock' });
        } finally {
          // Always close the channel to prevent resource leaks
          channel.close();
        }
      } catch (error) {
        logger.warn('Failed to broadcast lock event:', error);
      }

      logger.log('Screen locked successfully');
    } catch (error) {
      logger.error('Failed to lock screen:', error);
      // Still set as locked even if there's an error
      setIsLocked(true);
    }
  }, []);

  /**
   * Unlock the screen with passkey authentication
   * WebAuthn automatically falls back to PIN/password if biometric is unavailable
   */
  const unlockScreen = useCallback(async (): Promise<boolean> => {
    setUnlockInProgress(true);
    try {
      logger.log('Attempting to unlock screen with passkey/PIN...');

      // 1. Authenticate with passkey (or PIN fallback handled by OS).
      // The session-validity check used to run first, but the DB is closed
      // while we're locked — getDeviceToken() can't read the bearer until
      // beginEncryptedAccess opens the DB, so a "first" check would fall
      // back to cookie auth and bounce the user out the moment the
      // online-app cookie's inactivity window lapsed (the failure mode
      // this PR exists to fix). Unlock first; check after.
      const { key, rawId, authenticatorAttachment } = await authenticatePasskey();
      logger.log('Passkey/PIN authentication successful');

      // 2. Begin encrypted access with the derived key
      // The key is securely stored in the dexie-encrypted middleware closure
      await beginEncryptedAccess(key);
      logger.log('Encrypted access granted');
      // The user/person records are readable again — attribute Sentry events.
      void syncSentryUser();

      // 3. Now that the DB is open, the bearer is readable. Confirm the
      // session is still valid before exposing the unlocked UI. Skipped
      // when offline — offline-first PWA, the user can still use local
      // data without connectivity. setIsLocked stays true until the
      // check completes so a server-rejected bearer redirects through
      // softSignOut without ever briefly showing the unlocked app.
      if (typeof window !== 'undefined' && navigator.onLine) {
        logger.log('Checking session validity...');
        const sessionValid = await checkSessionValidity();
        if (!sessionValid) {
          logger.error('Session expired while locked');
          // Soft cleanup — do NOT wipe the DB. The user's unsynced field
          // work needs to survive a server-side revoke or token expiry so
          // they can re-authenticate and finish syncing. logout() (DB wipe)
          // is reserved for explicit user intent (the "Logout and wipe
          // device" confirmation flow) and the rule-2 UserNotFoundError
          // branch in useSessionValidator.
          await softSignOut();
          throw new Error('SESSION_EXPIRED');
        }
        logger.log('Session is valid');
      }

      // 3a. Silently migrate legacy person-scoped credentials. No-op when
      // the credential is already user-scoped. Failures are logged but do
      // not block the unlock — the user can still access their data with
      // the legacy key, and the migration will be retried on the next
      // unlock.
      try {
        await maybeMigrateLegacyCredential(rawId);
      } catch (error) {
        logger.error('Passkey migration failed (will retry on next unlock):', error);
      }

      // 4. Update lock state
      setIsLocked(false);

      // 4a. The encrypted database is open and the session is valid again:
      // let the data layer pull server changes and push anything edited
      // before the lock. (Reconnects that happened while locked were skipped
      // because every read would have failed on the closed database.)
      window.dispatchEvent(new Event(AppEvents.UNLOCKED));

      // 5. Reset activity timer
      resetTimer();

      // 6. Start a fresh session for this unlock. lockScreen() ended the
      // previous one and cleared current_session_id from IndexedDB, so
      // resumeSession() will fall through to startSession() and fire
      // session.start. resumeSession() also covers cold-start: when the
      // page reloads while locked, App.tsx's resumeSession effect can't
      // fire (currentPerson is null because the DB is closed), so the
      // SessionTracker singleton has no in-memory sessionId yet.
      try {
        // Tag the new session with the WebAuthn method actually used — platform
        // (Touch ID, Windows Hello) vs cross-platform (security key). Falls
        // through to startSession() because lockScreen() cleared the prior
        // session from IndexedDB.
        await getSessionTracker().resumeSession({ authMethod: authenticatorAttachment });
      } catch (error) {
        logger.warn('Failed to resume session tracking — continuing unlock', error);
      }

      // 7. Track storage metrics after unlock
      await trackStorageCheck('unlock');

      logger.log('Screen unlocked successfully');
      return true;
    } catch (error) {
      logger.error('Failed to unlock screen:', error);

      // If session expired, the logout already happened
      if (error instanceof Error && error.message === 'SESSION_EXPIRED') {
        return false;
      }

      return false;
    } finally {
      setUnlockInProgress(false);
    }
  }, [resetTimer]);

  /**
   * Register passkey authentication (WebAuthn with automatic PIN fallback)
   */
  const registerPasskey = useCallback(async (method?: PasskeyMethod): Promise<boolean> => {
    try {
      logger.log('Registering passkey/PIN authentication...');

      // Scope the passkey to user.id so it works across every org/person
      // this user belongs to. The current user record is set during the
      // auth callback and is always present in any authenticated context;
      // a missing record here means storage is corrupt, so fail loudly.
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        throw new Error('No current user available for passkey registration');
      }
      await registerPasskeyAuth(currentUser.id, method);
      logger.log('Passkey/PIN credential registered');

      // Immediately authenticate to get encryption key
      const { key } = await authenticatePasskey();
      logger.log('Initial passkey/PIN authentication successful');

      // Begin encrypted access - key is stored securely in dexie-encrypted middleware closure
      await beginEncryptedAccess(key);
      logger.log('Encrypted access granted');
      // The user/person records are readable again — attribute Sentry events.
      void syncSentryUser();

      // Update state
      setIsRegistered(true);
      setIsLocked(false);

      logger.log('Passkey/PIN registration complete');
      return true;
    } catch (error) {
      logger.error('Failed to register passkey/PIN:', error);
      // A PasskeyError carries a user-facing message (cancelled, not
      // supported on this device, ...). Rethrow it so the registration modal
      // can show that instead of a generic "try again".
      if (error instanceof PasskeyError) throw error;
      return false;
    }
  }, []);

  /**
   * Manual reset of activity timer
   */
  const resetActivityTimer = useCallback(() => {
    if (!isLocked && isRegistered) {
      resetTimer();
    }
  }, [isLocked, isRegistered, resetTimer]);

  const value: LockContextValue = {
    isLocked,
    isRegistered,
    unlockInProgress,
    lockScreen,
    unlockScreen,
    registerPasskey,
    resetActivityTimer,
    isLoading,
  };

  return <LockContext.Provider value={value}>{children}</LockContext.Provider>;
}

/**
 * Hook to access lock context
 * Must be used within LockProvider
 */
export function useLockContext(): LockContextValue {
  const context = useContext(LockContext);
  if (!context) {
    throw new Error('useLockContext must be used within LockProvider');
  }
  return context;
}
